import { Component, OnInit } from '@angular/core';
import { BookingService } from '@app/_services/booking.service';
import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, forkJoin, of } from 'rxjs';
import { debounceTime, switchMap, map, catchError } from 'rxjs/operators';
import { ceil } from 'lodash';
import { ProductPageService } from '@app/_services/productPage.service';
import { Router, RouterLink } from '@angular/router';
import { CurrencyPipe } from '@angular/common';
import { CurrencyConvertPipe } from '@app/shared/pipes';


@Component({
    selector: 'app-page-bookings',
    standalone: true,
    imports: [
        NgIf,
        NgFor,
        NgClass,
        DatePipe,
        PaginationComponent,
        MatIconModule,
        TranslateModule,
        RouterLink,
        CurrencyPipe,
        CurrencyConvertPipe
    ],
    templateUrl: './page-bookings.component.html',
    styleUrl: './page-bookings.component.scss'
})
export class PageBookingsComponent implements OnInit {
    bookings: any[] = [];
    paginationBookingCount = 1;
    pagination = { page: 1, limit: 10 };
    loading = false;
    paginationSubject$: Subject<any> = new Subject();

    constructor(
        private bookingService: BookingService,
        private productPageService: ProductPageService,
        private translate: TranslateService,
        private router: Router
    ) {}

    ngOnInit() {
        this.loadBookings();

        this.paginationSubject$
            .pipe(
                debounceTime(300),
                switchMap(() =>
                    this.bookingService.getUserBookings(
                        this.pagination.page,
                        this.pagination.limit
                    )
                )
            )
            .subscribe(response => {
                this.loading = false;
                if (response.code === 200 && response.bookings) {
                    this.bookings = response.bookings.reverse();

                    if (response.pagination) {
                        this.paginationBookingCount =
                            response.pagination.totalPages;
                    } else {
                        this.paginationBookingCount =
                            response.bookings.length === this.pagination.limit
                                ? Math.max(
                                      this.paginationBookingCount,
                                      this.pagination.page + 1
                                  )
                                : this.pagination.page;
                    }
                }
            });
    }

    loadBookings() {
        this.loading = true;
        this.bookingService
            .getUserBookings(this.pagination.page, this.pagination.limit)
            .pipe()
            .subscribe(res => {
                this.loading = false;
                if (res.code === 200 && res.bookings) {
                    this.bookings = res.bookings.reverse();

                    if (res.pagination) {
                        this.paginationBookingCount = res.pagination.totalPages;
                    } else {
                        this.paginationBookingCount =
                            res.bookings.length === this.pagination.limit
                                ? this.pagination.page + 1
                                : this.pagination.page;
                    }
                } else {
                    this.bookings = [];
                    this.paginationBookingCount = 1;
                }
            });
    }

    updateBookingsList(pageNumber: number) {
        this.pagination.page = pageNumber;
        this.loading = true;
        this.loadBookings();
    }

    // Helper methods with language support
    getProductName(booking: any): string {
        if (!booking.productName)
            return this.translate.instant('unknown_product');

        if (
            typeof booking.productName === 'object' &&
            booking.productName !== null
        ) {
            const currentLang = this.translate.currentLang || 'en';
            return (
                booking.productName[currentLang] ||
                booking.productName['en'] ||
                booking.productName['ar'] ||
                this.translate.instant('unknown_product')
            );
        }

        if (typeof booking.productName === 'string') {
            return booking.productName;
        }

        return this.translate.instant('unknown_product');
    }

    getCompanyName(booking: any): string {
        if (!booking.companyName)
            return this.translate.instant('unknown_company');

        if (
            typeof booking.companyName === 'object' &&
            booking.companyName !== null
        ) {
            const currentLang = this.translate.currentLang || 'en';
            return (
                booking.companyName[currentLang] ||
                booking.companyName['en'] ||
                booking.companyName['ar'] ||
                this.translate.instant('unknown_company')
            );
        }

        if (typeof booking.companyName === 'string') {
            return booking.companyName;
        }

        return this.translate.instant('unknown_company');
    }

    getStartDate(booking: any): string {
        const startDate =
            booking.startDate ||
            booking.selectedDate ||
            (booking.bookingDates && booking.bookingDates.length > 0
                ? booking.bookingDates[0]
                : null);

        return startDate ? this.formatDate(startDate) : 'N/A';
    }

    getBookingDaysCount(booking: any): number {
        if (booking.bookingDates && Array.isArray(booking.bookingDates)) {
            return booking.bookingDates.length;
        }
        if (booking.totalDays && booking.totalDays > 0) {
            return booking.totalDays;
        }
        return 1;
    }

    getTotalSlots(booking: any): number {
        if (booking.totalSlots && booking.totalSlots > 0) {
            return booking.totalSlots;
        }
        if (booking.timeSlots && Array.isArray(booking.timeSlots)) {
            return booking.timeSlots.length;
        }
        return booking.quantity || 1;
    }

    getPeriodDuration(booking: any): string {
        if (booking.totalDays && booking.totalDays > 0) {
            return booking.totalDays + ' ' + this.translate.instant('days');
        }

        if (booking.startDate && booking.endDate) {
            const start = new Date(booking.startDate);
            const end = new Date(booking.endDate);
            const diffTime = Math.abs(end.getTime() - start.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return diffDays + ' ' + this.translate.instant('days');
        }

        return 'N/A';
    }

    formatDate(date: string | Date): string {
        if (!date) return '';

        const dateObj = new Date(date);
        const currentLang = this.translate.currentLang || 'en';

        if (currentLang === 'ar') {
            // Arabic date format with Arabic month names and Arabic numerals
            const day = dateObj
                .getDate()
                .toString()
                .padStart(2, '0');
            const month = this.getArabicMonth(dateObj.getMonth());
            const year = dateObj.getFullYear().toString();

            return `${this.convertToArabicNumerals(
                day
            )} ${month} ${this.convertToArabicNumerals(year)}`;
        } else {
            // English date format
            return dateObj.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        }
    }

    formatTime(time: string): string {
        if (!time) return '';

        const currentLang = this.translate.currentLang || 'en';
        const [hours, minutes] = time.split(':');
        const timeObj = new Date();
        timeObj.setHours(parseInt(hours), parseInt(minutes));

        if (currentLang === 'ar') {
            // Arabic time format with Arabic numerals
            const englishTime = timeObj.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });

            return this.convertToArabicNumerals(englishTime)
                .replace('AM', 'ص')
                .replace('PM', 'م');
        } else {
            return timeObj.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        }
    }

    // Add this new method to get Arabic month names
    private getArabicMonth(monthIndex: number): string {
        const arabicMonths = [
            'يناير', // January
            'فبراير', // February
            'مارس', // March
            'أبريل', // April
            'مايو', // May
            'يونيو', // June
            'يوليو', // July
            'أغسطس', // August
            'سبتمبر', // September
            'أكتوبر', // October
            'نوفمبر', // November
            'ديسمبر' // December
        ];

        return arabicMonths[monthIndex] || arabicMonths[0];
    }

    // Converts Western numerals in a string to Arabic-Indic numerals
    private convertToArabicNumerals(input: string): string {
        const arabicNumerals = [
            '٠',
            '١',
            '٢',
            '٣',
            '٤',
            '٥',
            '٦',
            '٧',
            '٨',
            '٩'
        ];
        return input.replace(/\d/g, d => arabicNumerals[parseInt(d)]);
    }

    getStatus(booking: any): string {
        if (!booking.status) return '';
        return this.translate.instant('status_' + booking.status);
    }

    getStatusClass(booking: any): string {
        if (!booking.status) return '';

        const statusClasses: { [key: string]: string } = {
            pending: 'status-pending',
            approved: 'status-approved',
            rejected: 'status-rejected',
            cancelled: 'status-cancelled',
            completed: 'status-completed',
            inProgress: 'status-in-progress'
        };

        return statusClasses[booking.status] || '';
    }

    getBookingDate(booking: any): string {
        const bookingDate =
            booking.creationDate || booking.requestedAt || booking.createdAt;

        if (!bookingDate) return 'N/A';

        let date: Date;

        if (typeof bookingDate === 'object' && bookingDate.$date) {
            date = new Date(bookingDate.$date);
        } else {
            date = new Date(bookingDate);
        }

        if (isNaN(date.getTime())) {
            return 'N/A';
        }

        const currentLang = this.translate.currentLang || 'en';

        if (currentLang === 'ar') {
            // Arabic date and time format
            const day = date
                .getDate()
                .toString()
                .padStart(2, '0');
            const month = this.getArabicMonth(date.getMonth());
            const year = date.getFullYear().toString();

            const englishTime = date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });

            const arabicTime = this.convertToArabicNumerals(englishTime)
                .replace('AM', 'ص')
                .replace('PM', 'م');

            return `${this.convertToArabicNumerals(
                day
            )} ${month} ${this.convertToArabicNumerals(year)}, ${arabicTime}`;
        } else {
            return (
                date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                }) +
                ', ' +
                date.toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                })
            );
        }
    }
}
